import { CellContext, ColumnDef } from "@tanstack/react-table";
import { EditIcon, ReplaceIcon, TrashAltIcon } from "assets/icons";
import { DragDropChangeValue } from "components";
import { moveElementInArray } from "helpers";
import { t } from "i18next";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ItemsTable,
  ItemsTableDragDrop,
  PersonInfo,
  RowDragHandleCell,
  Status,
  TableItemActionsControl,
  TableItemStatusControl
} from "ui";

import { useDriverPriority, useUpdateDriver } from "../../apis";
import { DRIVER_STATUSES_LIST, DriverStatuses } from "../../constants";
import { Driver } from "../../drivers.types";
import { useDriversBasePath } from "../../hooks";
import { DriversTableProps } from "./DriversTable.types";

import styles from "./DriversTable.module.scss";

const DriversTable = ({
  driversList,
  sorting,
  setSorting,
  isLoading,
  onRowClick,
  onChangeDeleteStatus,
  enableDragDrop = false,
  setDrivers,
  isDragDisabled,
  isDeletedOnly
}: DriversTableProps) => {
  const navigate = useNavigate();
  const basePath = useDriversBasePath();
  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);
  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);

  const { mutate: mutateDriverPriority } = useDriverPriority();
  const updateDriver = useUpdateDriver({
    onSuccess: () => {
      setOpenedItemStatusesIndex(null);
    }
  });

  const onChangeDriverPriority = (value: DragDropChangeValue) => {
    if (!setDrivers) return;

    const { draggableId, toKeyForReorder, fromIndex, toIndex } = value;

    setDrivers(
      moveElementInArray({
        array: driversList,
        fromIndex,
        toIndex,
        keyForReorder: "priority"
      })
    );

    mutateDriverPriority({
      id: draggableId,
      moveToIndex: toKeyForReorder !== undefined ? toKeyForReorder : toIndex
    });
  };

  const handleManagerStatusUpdate = (isActive: boolean) => {
    if (openedItemStatusesIndex !== null) {
      updateDriver.mutate({
        driverId: driversList[openedItemStatusesIndex].id,
        isActive
      });
    }
  };

  const statusesList: Status[] = DRIVER_STATUSES_LIST.map((orStatus) => ({
    ...orStatus,
    onClick: () =>
      handleManagerStatusUpdate(orStatus.value === DriverStatuses.Active)
  }));

  const showDragColumns = enableDragDrop && !isDeletedOnly;

  const columns: Array<ColumnDef<Driver, Driver[]>> = useMemo(
    () => [
      ...(showDragColumns
        ? [
            {
              id: "order",
              header: "",
              size: 4,
              minSize: 4,
              cell: ({ row }: CellContext<Driver, Driver[]>) => (
                <RowDragHandleCell
                  rowId={row.id}
                  isDragDisabled={isDragDisabled}
                />
              )
            },
            {
              id: "priority",
              accessorKey: "priority",
              header: "#",
              size: 6,
              minSize: 6,
              cell: ({ row }: CellContext<Driver, Driver[]>) => row.index + 1
            }
          ]
        : []),
      {
        id: "name",
        accessorKey: "name",
        header: t("Driver"),
        size: showDragColumns ? 27 : 30,
        minSize: showDragColumns ? 27 : 30,
        cell: ({ row }) => (
          <PersonInfo
            image={row.original.image}
            personName={`${row.original.firstname} ${row.original.lastname}`}
            dimensions={{ width: 44, height: 44, borderRadius: "100px" }}
          />
        )
      },
      {
        header: t("Phone"),
        size: 17,
        minSize: 17,
        cell: ({ row }) => row.original.phoneNumber
      },
      {
        header: t("License ID"),
        size: 15,
        minSize: 15,
        cell: ({ row }) => (
          <span className={styles.driversTable_uppercase}>
            {row.original.licenseId}
          </span>
        )
      },
      {
        header: t("Plate Number"),
        size: 15,
        minSize: 15,
        cell: ({ row }) => (
          <span className={styles.driversTable_uppercase}>
            {row.original.plateNumber}
          </span>
        )
      },

      {
        id: "isActive",
        accessorKey: "isActive",
        header: t("Status"),
        size: 12,
        minSize: 12,
        cell: ({ row }: CellContext<Driver, Driver[]>) => (
          <TableItemStatusControl
            rowIndex={row.index}
            activeStatusIndex={row.original.isActive ? 0 : 1}
            openedItemStatusesIndex={openedItemStatusesIndex}
            setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
            disabled={row.original.isDeleted}
            statusesList={statusesList}
          />
        )
      },

      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: showDragColumns ? 4 : 7,
        minSize: showDragColumns ? 4 : 7,
        cell: ({ row }) => {
          const daysLeftToRestore = Math.ceil(
            moment(row.original.accountRestorationDeadline).diff(
              moment(),
              "days",
              true
            )
          );

          if (row.original.isDeleted && daysLeftToRestore <= 0) {
            return null;
          }

          return (
            <TableItemActionsControl
              actionsList={[
                ...(row.original.isDeleted
                  ? []
                  : [
                      {
                        label: "Edit",
                        Icon: (
                          <EditIcon fill={"#090914"} width={18} height={18} />
                        ),
                        onClick: () =>
                          navigate(`${basePath}/edit/${row.original.id}`)
                      }
                    ]),
                {
                  label: row.original.isDeleted ? t("Restore") : t("Delete"),
                  Icon: row.original.isDeleted ? (
                    <ReplaceIcon fill={"#090914"} width={17} height={17} />
                  ) : (
                    <TrashAltIcon fill={"#ff0033"} width={18} height={18} />
                  ),
                  labelColor: row.original.isDeleted ? "#090914" : "#ff0033",
                  onClick: () => onChangeDeleteStatus(row.original)
                }
              ]}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          );
        },
        enableSorting: false
      }
    ],
    [
      showDragColumns,
      isDragDisabled,
      openedItemOptionsIndex,
      openedItemStatusesIndex
    ]
  );

  if (enableDragDrop) {
    return (
      <ItemsTableDragDrop
        className={styles.driversTable}
        columns={columns}
        data={driversList}
        sorting={sorting}
        setSorting={setSorting}
        isLoading={isLoading}
        skeletonHeight={65}
        onRowClick={onRowClick}
        droppableId={"driversList"}
        keyForReorder={"priority"}
        onChange={onChangeDriverPriority}
        isDragDisabled={isDragDisabled}
      />
    );
  }

  return (
    <ItemsTable
      className={styles.driversTable}
      columns={columns}
      data={driversList}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={65}
      onRowClick={onRowClick}
    />
  );
};

export default DriversTable;
